/**
 * Modül Kontrolü — şirket bazlı modül aç/kapa tanımları.
 *
 * SuperAdmin bir şirketin kullanabileceği modülleri kısıtlayabilir
 * (Companies.EnabledModulesJson). Bu dosya tek doğruluk kaynağıdır:
 *  - Modül id'leri backend SuperAdminController.ValidModuleIds ve Sidebar
 *    bölüm id'leri ile birebir senkrondur.
 *  - Rota → modül eşlemesi hem MainLayout rota korumasında hem menü gate'lerinde kullanılır.
 *
 * Kurallar:
 *  - enabledModules === null → kısıtlama yok, her şey açık (geriye uyumlu varsayılan).
 *  - Bir path hiçbir modüle eşlenmemişse (profil, hızlı başlangıç vb.) her zaman erişilebilir.
 *  - Bir path birden çok modüle eşlenebilir (ör. resim kütüphanesi) — herhangi biri açıksa erişilir.
 */

export type ModuleId =
  | 'task_tracking'
  | 'accounting'
  | 'sales_system'
  | 'hr'
  | 'wms'
  | 'device_management'
  | 'card_management'
  | 'branches'
  | 'other';

export interface ModuleDefinition {
  id: ModuleId;
  labelTr: string;
  labelEn: string;
  descriptionTr: string;
}

export const MODULE_DEFINITIONS: ModuleDefinition[] = [
  { id: 'task_tracking', labelTr: 'Görev Takip', labelEn: 'Task Tracking', descriptionTr: 'Dashboard, görevler, raporlar, mesajlar, anketler' },
  { id: 'accounting', labelTr: 'Ön Muhasebe', labelEn: 'Pre-Accounting', descriptionTr: 'Cari, stok, banka, kasa, fatura, teklif, sipariş, irsaliye, çek/senet' },
  { id: 'sales_system', labelTr: 'Satış Sistemleri', labelEn: 'Sales Systems', descriptionTr: 'Hızlı satış, restoran, QR menü, kiosk, e-ticaret, kurye, entegrasyonlar' },
  { id: 'hr', labelTr: 'İnsan Kaynakları', labelEn: 'Human Resources', descriptionTr: 'Personel, vardiyalar, işe alım, CV başvuruları' },
  { id: 'wms', labelTr: 'Akıllı Depo (WMS)', labelEn: 'Smart Warehouse (WMS)', descriptionTr: 'Depo tanımlama, sipariş havuzu, iş emirleri, sayım' },
  { id: 'device_management', labelTr: 'Cihaz Yönetimi', labelEn: 'Device Management', descriptionTr: 'Bağlı cihazların yönetimi' },
  { id: 'card_management', labelTr: 'Kart & Cüzdan', labelEn: 'Card & Wallet', descriptionTr: 'Kart yönetimi ve oturumları' },
  { id: 'branches', labelTr: 'Şubeler', labelEn: 'Branches', descriptionTr: 'Şube yönetimi' },
  { id: 'other', labelTr: 'Diğer', labelEn: 'Other', descriptionTr: 'Rezervasyonlar, etkinlikler, mail raporları' },
];

export const ALL_MODULE_IDS: ModuleId[] = MODULE_DEFINITIONS.map((m) => m.id);

/**
 * Rota öneki → modül eşlemesi. Önek eşleşmesi segment duyarlıdır:
 * '/siparis' → '/siparis/satis/yeni' eşleşir ama '/siparis-yonetimi' EŞLEŞMEZ.
 * Daha uzun (spesifik) önekler önce kontrol edilir.
 */
const ROUTE_MODULE_MAP: { prefix: string; modules: ModuleId[] }[] = [
  // --- Spesifik istisnalar (önce) ---
  { prefix: '/accounting/mail-raporlari', modules: ['other'] },

  // --- Görev Takip ---
  { prefix: '/dashboard', modules: ['task_tracking'] },
  { prefix: '/tasks', modules: ['task_tracking'] },
  { prefix: '/reports', modules: ['task_tracking'] },
  { prefix: '/messages', modules: ['task_tracking'] },
  { prefix: '/surveys', modules: ['task_tracking'] },

  // --- İnsan Kaynakları ---
  { prefix: '/personnel', modules: ['hr'] },
  { prefix: '/shifts', modules: ['hr'] },
  { prefix: '/ik', modules: ['hr'] },
  { prefix: '/hr', modules: ['hr'] },

  // --- Ön Muhasebe ---
  { prefix: '/accounting', modules: ['accounting'] },
  { prefix: '/cari', modules: ['accounting'] },
  { prefix: '/cari-hareket-raporu', modules: ['accounting'] },
  { prefix: '/stok', modules: ['accounting'] },
  { prefix: '/stok-hareket-raporu', modules: ['accounting'] },
  { prefix: '/fiyat-degisim-loglari', modules: ['accounting'] },
  { prefix: '/basit-uretim-receteleri', modules: ['accounting'] },
  { prefix: '/urun-menuleri', modules: ['accounting'] },
  { prefix: '/sube-fiyatlari', modules: ['accounting'] },
  { prefix: '/etiket-yazdirma', modules: ['accounting'] },
  { prefix: '/banka', modules: ['accounting'] },
  { prefix: '/bank', modules: ['accounting'] },
  { prefix: '/kredi-kartlari', modules: ['accounting'] },
  { prefix: '/krediler', modules: ['accounting'] },
  { prefix: '/kredi-odemeleri', modules: ['accounting'] },
  { prefix: '/kasa', modules: ['accounting'] },
  { prefix: '/depo', modules: ['accounting'] },
  { prefix: '/doviz', modules: ['accounting'] },
  { prefix: '/currency', modules: ['accounting'] },
  { prefix: '/fatura', modules: ['accounting'] },
  { prefix: '/e-fatura', modules: ['accounting'] },
  { prefix: '/teklifler', modules: ['accounting'] },
  { prefix: '/irsaliyeler', modules: ['accounting'] },
  { prefix: '/irsaliye', modules: ['accounting'] },
  { prefix: '/siparisler', modules: ['accounting'] },
  { prefix: '/siparis', modules: ['accounting'] },
  { prefix: '/firmalar-arasi-b2b', modules: ['accounting'] },
  { prefix: '/cek-senet', modules: ['accounting'] },
  { prefix: '/kar-zarar', modules: ['accounting'] },
  { prefix: '/gider-turleri', modules: ['accounting'] },

  // --- Satış Sistemleri ---
  { prefix: '/hizli-satis', modules: ['sales_system'] },
  { prefix: '/restoran', modules: ['sales_system'] },
  { prefix: '/restaurant-orders', modules: ['sales_system'] },
  { prefix: '/restaurant-screen', modules: ['sales_system'] },
  { prefix: '/siparis-yonetimi', modules: ['sales_system'] },
  { prefix: '/tum-siparisler', modules: ['sales_system'] },
  { prefix: '/paket-ekrani', modules: ['sales_system'] },
  { prefix: '/gun-sonu', modules: ['sales_system'] },
  { prefix: '/gun-sonlari', modules: ['sales_system'] },
  { prefix: '/qr-menu-management', modules: ['sales_system'] },
  { prefix: '/qr-menu', modules: ['sales_system'] },
  { prefix: '/mutfak-ekrani-secimi', modules: ['sales_system'] },
  { prefix: '/mutfak-ekrani', modules: ['sales_system'] },
  { prefix: '/musteri-ekrani-yonetimi', modules: ['sales_system'] },
  { prefix: '/musteri-ekrani', modules: ['sales_system'] },
  { prefix: '/customer-screen', modules: ['sales_system'] },
  { prefix: '/kiosk-menu', modules: ['sales_system'] },
  { prefix: '/kiosk-products', modules: ['sales_system'] },
  { prefix: '/kulup-giris', modules: ['sales_system'] },
  { prefix: '/e-commerce', modules: ['sales_system'] },
  { prefix: '/kurye', modules: ['sales_system'] },
  { prefix: '/entegrasyonlar', modules: ['sales_system'] },
  { prefix: '/hacioglu-urun-eslestirme', modules: ['sales_system'] },

  // --- Paylaşılan (herhangi biri açıksa erişilir) ---
  { prefix: '/resim-kutuphanesi', modules: ['accounting', 'sales_system'] },

  // --- WMS ---
  { prefix: '/wms', modules: ['wms'] },

  // --- Cihaz / Kart / Şube / Diğer ---
  { prefix: '/cihaz-yonetimi', modules: ['device_management'] },
  { prefix: '/kart-yonetimi', modules: ['card_management'] },
  { prefix: '/branches', modules: ['branches'] },
  { prefix: '/events', modules: ['other'] },
  { prefix: '/reservations', modules: ['other'] },
];

/** Segment duyarlı önek eşleşmesi: '/siparis' ile '/siparis-yonetimi' eşleşmez. */
const pathMatchesPrefix = (pathname: string, prefix: string): boolean =>
  pathname === prefix || pathname.startsWith(`${prefix}/`);

/** Path'in ait olduğu modüller (eşleşme yoksa boş dizi = serbest sayfa). */
export const getModulesForPath = (pathname: string): ModuleId[] => {
  // En uzun (en spesifik) önek kazanır.
  let best: { prefix: string; modules: ModuleId[] } | null = null;
  for (const entry of ROUTE_MODULE_MAP) {
    if (pathMatchesPrefix(pathname, entry.prefix)) {
      if (!best || entry.prefix.length > best.prefix.length) best = entry;
    }
  }
  return best ? best.modules : [];
};

/** Modül açık mı? enabledModules null/undefined = kısıtlama yok. */
export const isModuleEnabled = (
  moduleId: ModuleId | string,
  enabledModules: string[] | null | undefined
): boolean => {
  if (enabledModules == null) return true;
  return enabledModules.includes(moduleId);
};

/** Path'e erişilebilir mi? Eşlenmemiş path'ler her zaman serbesttir. */
export const isPathAllowed = (
  pathname: string,
  enabledModules: string[] | null | undefined
): boolean => {
  if (enabledModules == null) return true;
  const modules = getModulesForPath(pathname);
  if (modules.length === 0) return true;
  return modules.some((m) => enabledModules.includes(m));
};
